import { useState, useEffect } from 'react';
import { writebackAPI } from '../../services/api';

export default function WritebackDashboard() {
	const [data, setData] = useState({ failed: [], stats: { successful: 0, failed: 0 } });
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [retrying, setRetrying] = useState(null);
	const days = 7;

	useEffect(() => {
		loadData();
	}, [days]);

	const loadData = async () => {
		try {
			setLoading(true);
			setError(null);
			const response = await writebackAPI.getStatus({ days });
			setData(response.data);
		} catch (err) {
			setError('Failed to load write-back status: ' + (err.message || 'Unknown error'));
		} finally {
			setLoading(false);
		}
	};

	const handleRetry = async (logId) => {
		try {
			setRetrying(logId);
			setError(null);
			await writebackAPI.retry(logId);
			// Reload data after retry
			await loadData();
		} catch (err) {
			setError('Retry failed: ' + (err.message || 'Unknown error'));
		} finally {
			setRetrying(null);
		}
	};

	const formatDate = (dateString) => {
		if (!dateString) return 'N/A';
		return new Date(dateString).toLocaleString();
	};

	const formatCurrency = (value) => {
		if (value == null) return '--';
		return `$${Number(value).toFixed(2)}`;
	};

	const total = data.stats.successful + data.stats.failed;
	const successRate = total > 0 ? Math.round((data.stats.successful / total) * 100) : 0;

	return (
		<div>
			<div className="page-header">
				<h1>DB Write-Back Status</h1>
				<button
					className="btn btn-secondary"
					onClick={loadData}
					disabled={loading}
				>
					Refresh
				</button>
			</div>

			{error && (
				<div className="alert alert-error">{error}</div>
			)}

			{/* Stats cards */}
			<div className="stat-grid" style={{ marginBottom: '1.5rem' }}>
				<div className="stat-item">
					<div className="stat-label">Last {days} Days</div>
					<div className="stat-value" style={{ color: 'var(--success-color, #16a34a)' }}>
						{data.stats.successful}
					</div>
					<div className="stat-label">Successful</div>
				</div>
				<div className="stat-item">
					<div className="stat-label">Last {days} Days</div>
					<div className="stat-value" style={{ color: data.stats.failed > 0 ? 'var(--danger-color, #dc2626)' : undefined }}>
						{data.stats.failed}
					</div>
					<div className="stat-label">Failed</div>
				</div>
				<div className="stat-item">
					<div className="stat-label">Success Rate</div>
					<div className="stat-value">{successRate}%</div>
					<div className="stat-label">{total} total attempts</div>
				</div>
				<div className="stat-item">
					<div className="stat-label">Pending Retry</div>
					<div className="stat-value" style={{ color: (data.failed?.length > 0) ? 'var(--warning-color, #f59e0b)' : undefined }}>
						{data.failed?.length || 0}
					</div>
					<div className="stat-label">Require attention</div>
				</div>
			</div>

			{/* Failed write-backs table */}
			<div className="form-section">
				<h2>Failed Write-Backs (Last {days} Days)</h2>

				{loading ? (
					<div className="loading">Loading...</div>
				) : data.failed?.length === 0 ? (
					<div className="alert alert-success">
						No failed write-backs in the last {days} days. All freight costs have been successfully written to the main database.
					</div>
				) : (
					<table className="simple-table">
						<thead>
							<tr>
								<th>Order ID</th>
								<th style={{ textAlign: 'right' }}>Sea Freight</th>
								<th style={{ textAlign: 'right' }}>Trucking</th>
								<th>Timestamp</th>
								<th>Retries</th>
								<th>Error</th>
								<th style={{ textAlign: 'center' }}>Action</th>
							</tr>
						</thead>
						<tbody>
							{data.failed?.map((log) => (
								<tr key={log.id}>
									<td>
										<code>{log.orderId?.substring(0, 8)}...</code>
									</td>
									<td style={{ textAlign: 'right' }}>
										{formatCurrency(log.seaFreightCost)}
									</td>
									<td style={{ textAlign: 'right' }}>
										{formatCurrency(log.truckingCost)}
									</td>
									<td>
										<span className="text-muted" style={{ fontSize: '0.75rem' }}>
											{formatDate(log.writtenAt)}
										</span>
									</td>
									<td>
										<span className={`badge ${log.retryCount >= 3 ? 'badge-danger' : 'badge-warning'}`}>
											{log.retryCount}
										</span>
									</td>
									<td>
										<span
											className="text-muted"
											title={log.errorMessage || 'Unknown error'}
											style={{
												maxWidth: '200px',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
												display: 'block',
												fontSize: '0.75rem'
											}}
										>
											{log.errorMessage || 'Unknown error'}
										</span>
									</td>
									<td style={{ textAlign: 'center' }}>
										<button
											className="btn btn-sm btn-secondary"
											onClick={() => handleRetry(log.id)}
											disabled={retrying === log.id}
										>
											{retrying === log.id ? 'Retrying...' : 'Retry'}
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>
		</div>
	);
}
